import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  providerApi,
  type CreateProviderStaffInput,
  type ProviderMembershipRole,
  type ProviderTeamMember,
  isOfflineApiError,
} from "../../lib/api";
import {
  createOfflineRecordId,
  writeCachedApiResponse,
} from "../../lib/offline-store";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderOffline } from "../../lib/provider-offline";

function getErrorDetail(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  return fallback;
}

export function useProviderTeam() {
  const { t } = useAppPreferences();
  const { queueMutation, syncVersion } = useProviderOffline();
  const [members, setMembers] = useState<ProviderTeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [updatingVenueUserId, setUpdatingVenueUserId] = useState<string | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);

  const persistMembers = useCallback((nextMembers: ProviderTeamMember[]) => {
    setMembers(nextMembers);
    writeCachedApiResponse("/api/v1/provider/team", {
      members: nextMembers,
    });
  }, []);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await providerApi.listTeam();
      setMembers(response.members);
    } catch (requestError) {
      setError(getErrorDetail(requestError, t("Unable to load provider team.")));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (syncVersion === 0) {
      return;
    }

    void reload();
  }, [reload, syncVersion]);

  const createMember = useCallback(
    async (payload: CreateProviderStaffInput) => {
      setCreating(true);
      setError(null);
      setNotice(null);

      try {
        const member = await providerApi.createTeamMember(payload);
        await reload();
        setNotice(t("Added {name} to the provider team.", { name: member.display_name ?? member.email }));
        return true;
      } catch (requestError) {
        if (isOfflineApiError(requestError)) {
          const member: ProviderTeamMember = {
            user_id: createOfflineRecordId("staff"),
            email: payload.email,
            display_name: payload.display_name,
            role: "provider_staff",
            membership_role: payload.role ?? "front_desk",
            is_active: true,
            venue_ids: payload.venue_ids ?? null,
          };
          persistMembers([...members, member]);
          queueMutation({
            method: "POST",
            path: "/api/v1/provider/team",
            body: payload,
            label: `Create team member ${payload.email}`,
            local_resource_id: member.user_id,
            response_id_field: "user_id",
          });
          setNotice(
            t("Saved {name} offline. Access will sync when connection returns.", { name: payload.display_name }),
          );
          return true;
        }

        const detail = getErrorDetail(
          requestError,
          t("Unable to add provider staff."),
        );
        setError(detail);
        return false;
      } finally {
        setCreating(false);
      }
    },
    [members, persistMembers, queueMutation, reload, t],
  );

  const updateMemberVenues = useCallback(
    async (userId: string, venueIds: string[] | null) => {
      setUpdatingVenueUserId(userId);
      setError(null);
      setNotice(null);

      try {
        const updatedMember = await providerApi.updateTeamMemberVenues(userId, {
          venue_ids: venueIds,
        });
        persistMembers(
          members.map((member) =>
            member.user_id === userId ? updatedMember : member,
          ),
        );
        setNotice(t("Updated staff venue access."));
        return true;
      } catch (requestError) {
        if (isOfflineApiError(requestError)) {
          persistMembers(
            members.map((member) =>
              member.user_id === userId ? { ...member, venue_ids: venueIds } : member,
            ),
          );
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/team/${userId}/venues`,
            body: { venue_ids: venueIds },
            label: "Update staff venue access",
          });
          setNotice(t("Saved staff venue access offline. It will sync when connection returns."));
          return true;
        }

        setError(getErrorDetail(requestError, t("Unable to update staff venue access.")));
        return false;
      } finally {
        setUpdatingVenueUserId(null);
      }
    },
    [members, persistMembers, queueMutation, t],
  );

  const updateMemberRole = useCallback(
    async (userId: string, role: ProviderMembershipRole) => {
      setUpdatingRoleUserId(userId);
      setError(null);
      setNotice(null);

      try {
        const updatedMember = await providerApi.updateTeamMemberRole(userId, { role });
        persistMembers(
          members.map((member) =>
            member.user_id === userId ? updatedMember : member,
          ),
        );
        setNotice(t("Updated team role."));
        return true;
      } catch (requestError) {
        if (isOfflineApiError(requestError)) {
          persistMembers(
            members.map((member) =>
              member.user_id === userId ? { ...member, membership_role: role } : member,
            ),
          );
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/team/${userId}/role`,
            body: { role },
            label: "Update team member role",
          });
          setNotice(t("Saved team role offline. It will sync when connection returns."));
          return true;
        }

        setError(getErrorDetail(requestError, t("Unable to update team role.")));
        return false;
      } finally {
        setUpdatingRoleUserId(null);
      }
    },
    [members, persistMembers, queueMutation, t],
  );

  const revokeMember = useCallback(
    async (userId: string) => {
      setRevokingId(userId);
      setError(null);
      setNotice(null);

      try {
        const member = members.find((value) => value.user_id === userId);
        await providerApi.revokeTeamMember(userId);
        await reload();
        setNotice(t("Revoked {name}.", { name: member?.display_name ?? member?.email ?? t("team member") }));
        return true;
      } catch (requestError) {
        if (isOfflineApiError(requestError)) {
          const member = members.find((value) => value.user_id === userId);
          if (!member) {
            setError(t("This team member is not cached for offline updates."));
            return false;
          }

          persistMembers(
            members.map((value) =>
              value.user_id === userId ? { ...value, is_active: false } : value,
            ),
          );
          queueMutation({
            method: "DELETE",
            path: `/api/v1/provider/team/${userId}`,
            body: null,
            label: `Revoke team member ${member.email}`,
          });
          setNotice(
            t("Revoked {name} offline. The change will sync when connection returns.", { name: member.display_name ?? member.email }),
          );
          return true;
        }

        const detail = getErrorDetail(
          requestError,
          t("Unable to revoke provider staff access."),
        );
        setError(detail);
        return false;
      } finally {
        setRevokingId(null);
      }
    },
    [members, reload, t],
  );

  return {
    members,
    isLoading,
    error,
    notice,
    creating,
    revokingId,
    updatingVenueUserId,
    updatingRoleUserId,
    createMember,
    updateMemberVenues,
    updateMemberRole,
    revokeMember,
  };
}
